import {
  MediaData,
  MediaPickerComponent,
  MediaType,
  NewMessage,
  PickerMode,
  Severity,
} from '@gripp/shared-logic';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleProp, ViewStyle } from 'react-native';
import {
  ChatComposerCard,
  useComposerLocation,
} from '../chat/chatComposerCard';
import { useIssueFooterDraft } from './useIssueFooterDraft';
import { IssueFooterMobile } from './issueFooterMobile';

export type IssueFooterProps = {
  active?: boolean;
  onNewMessage: (message: NewMessage) => Promise<void>;
  mediaPicker: MediaPickerComponent;
  acceptedMediaTypes?: MediaType[];
  workspaceId?: string;
  /**
   * The activity being composed in, if any. Forwarded to the mention field so
   * a guest who has a live grant on this activity is mentionable inside it;
   * omit for a not-yet-created activity (there is nothing to grant against).
   */
  issueId?: string;
  mode?: PickerMode;
  containerStyle?: StyleProp<ViewStyle>;
  maxImagePreviewWidth?: number;
  focusOnMessage?: boolean;
  /** Placeholder text for the compose input. Defaults to t('common.message'). */
  placeholder?: string;
  pickDocument?: (callbacks: {
    setUploading: (uploading: boolean) => void;
    onProgress: (mediaId: string, progress: number) => void;
  }) => Promise<MediaData | null>;
  /**
   * Mobile only. Summarising is contextual: the composer renders its AI icon
   * only where a host actually has something to summarise, so leave this unset
   * (as every caller does today, there being no summarise service yet) and the
   * icon stays hidden rather than presenting a dead control.
   */
  onSummarize?: () => void;
  /** Native asset Chat only, after flag/access admission. The host captures the
   * severity when onNewMessage starts and resets it only on successful posting,
   * provided that selection has not changed in the meantime. Threads omit this. */
  chatPosting?: {
    severity?: Severity;
    onSeverityChange: (severity?: Severity) => void;
  };
  /**
   * Web only: lets a message change its activity's type as it posts, through
   * the send menu. `onChange` runs after the post succeeds, so a failed post
   * changes nothing. Omit where the type cannot be changed.
   */
  sendAs?: {
    current?: Severity;
    onChange: (severity: Severity) => void;
  };
};

/**
 * Platform router: native gets the redesigned pill-to-card composer
 * (issueFooterMobile.tsx, Figma V6 "Text Navigation"); web gets the same card as
 * the asset chat (chatComposerCard.tsx).
 */
export const IssueFooter = (props: IssueFooterProps) =>
  Platform.OS === 'web' ? (
    <IssueFooterWeb
      key={JSON.stringify([props.workspaceId, props.issueId])}
      {...props}
    />
  ) : (
    <IssueFooterMobile {...props} />
  );

const IssueFooterWeb = (props: IssueFooterProps) => {
  const { t } = useTranslation();
  const [sendAsSeverity, setSendAsSeverity] = useState<Severity>();
  const selectedSeverity = useRef(sendAsSeverity);
  selectedSeverity.current = sendAsSeverity;
  const requestLocation = useComposerLocation();

  const {
    handleAdd,
    handleUploadProgress,
    messageMedia,
    handleDelete,
    message,
    setMessage,
    canSend,
    setUploading,
    send: submit,
  } = useIssueFooterDraft(async (newMessage) => {
    const severity = selectedSeverity.current;
    await props.onNewMessage(newMessage);
    if (severity) {
      props.sendAs?.onChange(severity);
      // A type picked while this post was in flight belongs to the next one.
      if (selectedSeverity.current === severity) setSendAsSeverity(undefined);
    }
  }, props.active);

  // The link goes in the text rather than beside it because a message, unlike a
  // new activity, has nowhere else to carry a location. Same as issueFooterMobile.
  const onLocationPress = async () => {
    const fix = await requestLocation();
    if (!fix) return;
    setMessage((current) =>
      current.length > 0 && !current.endsWith(' ')
        ? `${current} ${fix.url}`
        : `${current}${fix.url}`
    );
  };

  return (
    <ChatComposerCard
      message={message}
      onMessageChange={setMessage}
      onSend={() => void submit()}
      canSend={canSend}
      placeholder={props.placeholder ?? t('common.message')}
      issueId={props.issueId}
      media={messageMedia}
      onMediaAdded={handleAdd}
      onMediaDeleted={handleDelete}
      onUploadProgress={handleUploadProgress}
      onUploadingChange={setUploading}
      mediaPicker={props.mediaPicker}
      workspaceId={props.workspaceId}
      acceptedMediaTypes={props.acceptedMediaTypes}
      onLocationPress={() => void onLocationPress()}
      sendAs={
        props.sendAs && {
          severity: sendAsSeverity,
          onChange: setSendAsSeverity,
          current: props.sendAs.current,
        }
      }
      containerStyle={props.containerStyle}
    />
  );
};
